import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, InputGroup, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import HighlightOff from 'mdi-react/HighlightOffIcon';
import Search from 'mdi-react/SearchIcon';

import { defaultGridState } from 'modules/common/constants';
import { MIN_FILTER_VALUE_LENGTH } from 'modules/security/containers/TripsTabContainer/constants';
import { DataGrid, DataGridColumn, FilterOperators, LogicTypes } from 'modules/shared/DataGrid';
import { useGridState } from 'modules/shared/DataGrid/hooks/useGridState';
import { Filter, GridFilter } from 'modules/shared/DataGrid/utils/interfaces';
import SmallSpinner from 'modules/shared/SmallSpinner';
import { RootState } from 'rootReducer';
import { clientDateFormat } from '../../../../entities/Trip';
import { SecurityReportProps } from '../interfaces';
import { getSecurityUnloadingReportsRequest } from './actions';
import ReportDepCell from '../cell/ReportIDepCell';
import { fieldsNames } from './constants';
import ReportToTonn from '../cell/ReportToTonn';
import ReportUnloadCell from '../cell/ReportUnloadCell';
import ReportDeviation from '../cell/ReportDeviation';
import ReportArrivCell from '../cell/ReportArrivCell';
import classes from './ReportUnloading.module.scss';
import ReportBookingDate from '../cell/ReportBokingDate';

export const ReportUnloading: React.FC<SecurityReportProps> = ({ endDate, startDate, onRow }) => {
  const [filterInputValue, setFilterInputValue] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const branchLocal = localStorage.getItem('branchId') || 'null';
  const branchId = JSON.parse(branchLocal)?.id;
  const { reports, loading } = useSelector(
    (state: RootState) => state.securityModuleReducer.reportsReducer.reportUnloadingReducer,
  );
  useEffect(() => {
    if (branchId && endDate && startDate) {
      dispatch(getSecurityUnloadingReportsRequest(startDate, endDate, `${branchId}`));
    }
  }, [startDate, endDate]);

  const gridState = useGridState(reports, defaultGridState);
  useEffect(() => {
    if (filterInputValue.length < MIN_FILTER_VALUE_LENGTH) {
      gridState.setFilter({
        logic: LogicTypes.and,
        filters: [],
      });
    } else {
      const newFilter = {
        logic: LogicTypes.or,
        filters: fieldsNames.map(
          (name): Filter => ({
            operator: FilterOperators.contains,
            value: filterInputValue,
            field: name,
          }),
        ),
      } as GridFilter;
      gridState.setFilter(newFilter);
    }
  }, [filterInputValue]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterInputValue(e.target.value);
  };

  const handleClearIconClick = () => {
    setFilterInputValue('');
    if (filterInputRef.current) {
      filterInputRef.current.focus();
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col className="d-flex align-items-center justify-content-end pb-3">
          <InputGroup className={classes.filter}>
            <Form.Control as="input" type="text" name="filter" onChange={handleFilterChange} ref={filterInputRef} />
            <Search size={18} className={classes.searchIcon} />
            {!!filterInputValue && (
              <HighlightOff size={18} className={classes.clearFilterIcon} onClick={handleClearIconClick} />
            )}
          </InputGroup>
        </Col>
      </Row>
      {loading ? (
        <SmallSpinner />
      ) : (
        <DataGrid
          pageable
          data={gridState.gridData}
          sort={gridState.sort}
          total={gridState.total}
          skip={gridState.pagination?.skip}
          pageSize={gridState.pagination?.pageSize}
          onPageChange={gridState.handlePageChange}
          onSortChange={gridState.handleSortChange}
          onRow={onRow}
          className={classes.tablePrint}
        >
          <DataGridColumn title="Рейс" dataIndex="tripId" key="tripId" width="60px" sortable />
          <DataGridColumn
            title="Дата бронювання"
            dataIndex="bookingDate"
            key="bookingDate"
            render={ReportBookingDate}
            width="10%"
            className={classes.bookingDate}
          />
          <DataGridColumn title="Держномер ТЗ" dataIndex="vehicleLicensePlate" key="vehicleLicensePlate" width="60px" />
          <DataGridColumn
            title="Держномер причепа"
            dataIndex="trailerLicensePlate"
            key="trailerLicensePlate"
            width="60px"
          />
          <DataGridColumn title="Водій" dataIndex="driverName" key="driverName" width="5%" />
          <DataGridColumn
            title="Номер посвідчення"
            dataIndex="driverDocumentNumber"
            key="driverDocumentNumber"
            width="5%"
          />
          <DataGridColumn title="Телефон" dataIndex="driverPhone" key="driverPhone" width="5%" />
          <DataGridColumn
            sortable
            title="Дані в'їзду"
            dataIndex="entryDateTime"
            key="entryDateTime"
            dateFormat={clientDateFormat}
            render={ReportArrivCell}
            width="5%"
          />
          <DataGridColumn
            sortable
            title="Дані виїзду"
            dataIndex="departureDateTime"
            key="departureDateTime"
            dateFormat={clientDateFormat}
            render={ReportDepCell}
            width="5%"
          />
          <DataGridColumn
            title="Вага в'їзду, т"
            dataIndex="entryWeight"
            key="entryWeight"
            render={ReportToTonn}
            width="5%"
          />
          <DataGridColumn
            title="Вага виїзду, т"
            render={ReportToTonn}
            dataIndex="departureWeight"
            key="departureWeight"
            width="5%"
          />
          <DataGridColumn title="Деталі вантажу" dataIndex="packingLists" key="packingLists" width="5%" />
          <DataGridColumn title="Сума по TTH, т" dataIndex="ttnSum" key="ttnSum" width="5%" />
          <DataGridColumn
            title="Вивантажено, т"
            dataIndex="unloaded"
            key="unloaded"
            render={ReportUnloadCell}
            width="5%"
          />
          <DataGridColumn
            title="Відхилення, т"
            dataIndex="deviation"
            key="deviation"
            render={ReportDeviation}
            width="5%"
          />
          <DataGridColumn title="Експедитор" dataIndex="forwarderName" key="forwarderName" width="5%" />
          <DataGridColumn title="Телефон" dataIndex="forwarderPhone" key="forwarderPhone" width="5%" />
          <DataGridColumn title="Коментар охорони" dataIndex="securityMessage" key="securityMessage" width="5%" />
          <DataGridColumn title="Номер Заводу" dataIndex="factoryNumber" key="factoryNumber" width="5%" /> {/* new */}
          <DataGridColumn title="Номер Складу" dataIndex="warehouseCode" key="warehouseCode" width="5%" />
          <DataGridColumn
            title="Номер вихідного постачання"
            dataIndex="outputSupplyNumber"
            key="outputSupplyNumber"
            width="5%"
          />
          <DataGridColumn
            title="Кількість позицій у наряді"
            dataIndex="orderItemsCount"
            key="orderItemsCount"
            width="5%"
          />
        </DataGrid>
      )}
    </React.Fragment>
  );
};
